import * as React from "react";
import { cn } from "@/lib/utils";

interface InputV2Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
}

export function InputV2({
  label,
  helperText,
  errorText,
  successText,
  className,
  id,
  "aria-describedby": externalDescribedBy,
  ...props
}: InputV2Props) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const errorId = errorText ? `${inputId}-error` : undefined;
  const helperId = !errorText && helperText ? `${inputId}-helper` : undefined;
  const describedBy =
    [externalDescribedBy, errorId, helperId].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className="block space-y-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-[var(--v2-text-strong)]"
        >
          {label}
        </label>
      ) : null}
      <input
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        className={cn(
          "v2-focus-ring min-h-10 w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white px-3 text-sm text-[var(--v2-text-strong)] placeholder:text-slate-400",
          errorText ? "border-red-300" : "",
          !errorText && successText ? "border-emerald-300" : "",
          className
        )}
        {...props}
      />
      {errorText ? (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {errorText}
        </span>
      ) : null}
      {!errorText && helperText ? (
        <span id={helperId} className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      ) : null}
      {!errorText && !helperText && successText ? (
        <span className="text-xs text-emerald-600">{successText}</span>
      ) : null}
    </div>
  );
}
